import { LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { gqlSystemRoleCode as SYSTEM_ROLE } from '@minecraft.graphql-types';
import { TokenParsed } from '@minecraft.utils';
import { ArtistSubscriptionFragment, TrialType } from '@minecraft.graphql-operations';
import {
  getDurationBetweenDates,
  isValidDate,
  subtractFromDate,
  isDateInPast,
  isDateInFuture,
} from '@minecraft.date-utils';
import { getAccessToken } from '../Auth/utils/authStorage';

const parseBase64 = <T = any>(encodedString: string): T => {
  return JSON.parse(window.atob(encodedString));
};

const parseJwt = (token: string): TokenParsed | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    return parseBase64(base64);
  } catch (e) {
    return null;
  }
};

export enum FEATURE_FLAGS {
  ALACARTE_MEDIA_PURCHASE = 'a-la-carte-media-purchase',
  AUDITIONS = 'web-auditions',
  BE_COUPON_CODE = 'be-apply-account-coupon-code',
  CAST_IT_STUDIO_PHASE_1 = 'web-cast-it-studio-phase-1',
  EXPERIMENT_OPPORTUNITY_COST = 'experiment-opportunity-cost',
  EXPERIMENT_PRICE_FRAMING_LANGUAGE = 'experiment-price-framing-language',
  EXPERIMENT_TESTIMONIALS_INTEGRATION = 'experiment-testimonials-integration',
  EXPERIMENT_UPGRADE_MODAL = 'experiment-upgrade-modal',
  FE_COUPON_CODE = 'web-account-coupon-code',
  IMPACTED_SERVICE = 'web-impacted-service',
  PAYPAL_INTEGRATION = 'paypal-integration',
  QA_VIEW_MAINTENANCE = 'web-qa-maintenance-flag',
  ROLE_SUBMISSION_MEDIA_SESSION = 'role-submission-media-session',
  SCHEDULED_MAINTENANCE = 'web-scheduled-maintenance-flag',
  SUBMISSION_TRENDS = 'web-submission-trends',
  SINGLE_CLICK_REACTIVATION = 'single-click-reactivation',
  TALENT_SCOUT_PHASE_1 = 'web-talent-scout-phase-1',
  ULTIMATE_SUBSCRIPTION_PLAN = 'ultimate-subscription-plan',
  UNSCHEDULED_MAINTENANCE = 'web-unscheduled-maintenance-flag',
  VIRTUAL_AUDITIONS = 'web-virtual-auditions',
  WEB_AUGEO_BENEFITS = 'web-augeo-benefits',
  WEB_CASTING_COLLABORATORS = 'web-casting-collaborators',
  WEB_CASTING_OPEN_CALL_ABOUT_SECTION = 'web-casting-open-call-about-section',
  WEB_CASTING_OPEN_CALL_P1 = 'web-casting-open-call-p1',
  WEB_CASTING_OPEN_CALL_REF_PHOTO = 'web-casting-open-call-ref-photo',
  WEB_CASTING_PRIMARY_WORKING_FILTER = 'web-casting-primary-working-filter',
  WEB_CASTING_PROJECT_EMAIL_HISTORY = 'web-casting-project-email-history',
  WEB_CASTING_SCHEDULE_SENDTIMES_OPTIONS = 'web-casting-schedule-time-options',
  WEB_CBILL_SPLIT_SCREEN = 'web-cbill-split-screen',
  WEB_CUSTOM_URL = 'web-custom-url',
  WEB_DEFAULT_UPGRADE_MODAL_TO_ANNUAL = 'web-default-upgrade-modal-to-annual',
  WEB_ENABLE_TURNSTILE_VALIDATION = 'web-enable-turnstile-validation',
  WEB_FORMS = 'web-forms',
  WEB_GOOGLE_SSO = 'web-google-sso',
  WEB_IOS_INSTALL_APP_BANNER = 'web-ios-install-app-banner',
  WEB_LEGAL_LANGUAGE = 'web-legal-language',
  WEB_LILO_P1 = 'web-lilo-p1',
  WEB_LILO_ROSTER_MEDIA_REPORT = 'web-lilo-roster-media-report',
  WEB_NEW_MOBILE_REGISTRATION = 'web-new-mobile-registration-flow-experiment',
  WEB_NEW_TRIAL_PAYWALL = 'web-new-trial-paywall',
  WEB_ONBOARDING_DEPRECATION = 'web-onboarding-deprecation',
  WEB_OPEN_CALL_LANDING_PAGE = 'web-open-call-landing-page',
  WEB_OPEN_CALL_LOGO = 'web-casting-open-call-logo',
  WEB_OPEN_CALLS = 'web-open-call-role',
  WEB_PAYWALL_BILLING_ADDRESS = 'web-paywall-billing-address',
  WEB_PRESENTATION_FORMS_EXPORT = 'web-presentation-forms-export',
  WEB_PROJECT_LOCKBOX_P1 = 'web-project-lockbox-p1',
  WEB_REDWOOD_VIDEO_PRESET = 'web-redwood-video-upload-preset',
  WEB_REGISTRATION_PROMO_CODE_REDEMPTION = 'web-registration-promo-code-redemption',
  WEB_ROLE_TRACKER = 'web-role-tracker',
  WEB_SCHEDULE_REPORT_TALENT_NUMBER_OPTION = 'web-schedule-report-talent-number-option',
  WEB_SESSION_SIZE_SHEET_REPORT = 'web-session-size-sheet-report',
  WEB_TALENT_OPEN_CALL_FILTER = 'web-talent-open-call-filter',
  WEB_WATERMARKING_SIDES = 'web-watermarking-sides',
  WEB_LILO_APPUOS = 'web-lilo-appuos',
  WEB_COPY_TO_CAST_IT_PAGE = 'web-copy-to-cast-it-page',
  WEB_COPY_TO_CASTIT = 'web-copy-to-castit',
  WEB_COPY_TO_CASTIT_STUDIO = 'web-copy-to-castit-studio',
  WEB_GROUP_DETAIL_COMBO = 'web-casting-group-detail-combo',
  WEB_CASTING_EXTERNAL_INVITATION_ENABLED = 'web-casting-external-invitation-enabled',
  WEB_CASTING_OVERLAY_AUDITION_HIST_THUMBNAIL = 'web-casting-overlay-audition-hist-thumbnail',
  WEB_CASTING_PRES_SIDEBAR_VIEW = 'web-casting-pres-sidebar-view',
  WEB_PHOTO_THUMB_CROP_V2 = 'web-photo-thumb-crop-v2',
  WEB_CASTING_ADD_TO_MEDIA_FROM_SCHEDULE = 'web-casting-add-to-media-from-schedule',
  WEB_CASTING_STUDIO_PROJECT_SYNC = 'web-casting-studio-project-sync',
  WEB_SELF_TAPE_STRINGS = 'web-self-tape-strings',
  WEB_CASTING_COPY_TO_NEW_SESSION = 'web-casting-copy-to-new-session',
  MOBILE_CASTING_COPY_TO_SESSION = 'mobile-casting-copy-to-session',
  WEB_CANCEL_CONFIRMED_MEDIA_REQ = 'web-cancel-confirmed-media-request',
  WEB_REPS_SUBMIT_SELECTS_ON_ALL_ROLES = 'web-reps-submit-selects-on-all-roles',
  WEB_REPS_THEATRICAL_PROJECT_REPORT = 'web-reps-theatrical-project-report',
  WEB_CASTING_LIVE_SHARE_SELECTS = 'web-casting-live-share-selects',
  WEB_LOCATIONS_WITHOUT_DATES = 'web-locations-without-dates',
  WEB_CASTING_FULL_BLEED_BASEBALL_CARD_PHOTO = 'web-casting-full-bleed-baseball-card-photo',
  WEB_HEADSHOTS_DIRECT_BOOKING = 'web-headshots-direct-booking',
}

// These are features that have been shipped and are in transition of being removed
// During this time we want to be sure users are getting the absolute value of the flag
// even if they have turned off the LD client calls in their browser
// These should only be set when the feature has been shipped and is in the process of being removed
//
// These are set to "true" and pushed to production. Then the code can be incrementally removed with less risk
export const MIGRATED_FEATURE_FLAGS = {
  // Example: `[SUBMISSION_TRENDS]: true` would permanently set that flag to true and tests would still pass
};

export const MAINTENANCE_OVERRIDE_COOKIE_VALUE = 'maintenance_override';

export const canSkipMaintenanceGuard = () => {
  // Getting cookie that will allow super-user to bypass splash screen for tests
  const maintModeCookie = Object.fromEntries(
    document.cookie
      .split(/; */)
      .filter((row) => row.startsWith(MAINTENANCE_OVERRIDE_COOKIE_VALUE))
      .map((cookie) => cookie.split('=', 2))
  );

  return maintModeCookie?.[MAINTENANCE_OVERRIDE_COOKIE_VALUE] === 'true';
};

const getSecondaryKey = (parsedToken: TokenParsed) => {
  // secondary attribute for a TALENT is artistId
  if (parsedToken?.role?.code === SYSTEM_ROLE.TALENT) {
    return parsedToken?.artistId?.toString();
  }

  // for all other users, user their organizationId
  return parsedToken?.organizationId?.toString();
};

const getSubscriptionBillingCode = (subscription?: ArtistSubscriptionFragment) => {
  if (subscription?.trialType === TrialType.Cardless) return `${subscription.plan?.billingCode}_cardless_trial`;
  return subscription?.plan?.billingCode;
};

// trialStartedAt = trialEndsAt - trialLengthDays; then full elapsed days from that start to now (Math.floor).
// Day N begins only after N * 24 hours have passed, so a cutoff of day 10 is exactly 240 hours.
export const getDaysSinceTrialStarted = (activeSubscription?: ArtistSubscriptionFragment): number | undefined => {
  const trialEndsAt = activeSubscription?.trialEndsAt;
  // subscription.trialLengthDays is the length offered for this subscription (may differ from plan.trialLengthDays)
  const trialLengthDays = activeSubscription?.trialLengthDays;
  const trialStartedAt = subtractFromDate({ amount: trialLengthDays, unit: 'days', date: trialEndsAt });
  const trialHasNotStarted = isDateInFuture({ isoDateString: trialStartedAt });
  const trialHasEnded = isDateInPast({ isoDateString: trialEndsAt });

  if (!isValidDate(trialStartedAt) || !isValidDate(trialEndsAt) || trialHasNotStarted || trialHasEnded) {
    return undefined;
  }

  const durationBetweenDates = getDurationBetweenDates({ startDate: trialStartedAt, roundAs: 'floor' });
  if (Number.isNaN(durationBetweenDates.asDays)) {
    return undefined;
  }
  return durationBetweenDates.asDays;
};

// this looks up the user's access token out of localstorage by default
export const buildFeatureUser = ({
  token,
  activeSubscription,
  previousSubscription,
  paywallLocation,
  countryCode,
  accountCountryCode,
  daysSinceLastSubscription,
}: {
  token?: string;
  activeSubscription?: ArtistSubscriptionFragment;
  previousSubscription?: ArtistSubscriptionFragment;
  paywallLocation?: string;
  countryCode?: string;
  accountCountryCode?: string;
  daysSinceLastSubscription?: number;
} = {}) => {
  const accessToken = token || getAccessToken();
  const user: LDSingleKindContext = {
    kind: 'user',
    anonymous: !accessToken,
    isQa: canSkipMaintenanceGuard(),
    currentLocationCode: countryCode,
  };

  if (accessToken) {
    const parsed = parseJwt(accessToken);

    user.email = parsed?.email;
    user.key = parsed?.id?.toString();
    user.secondary = getSecondaryKey(parsed);
    user.organizationId = parsed?.organizationId?.toString();
    user.artistId = parsed?.artistId?.toString();
    user.role = parsed?.role?.code;
    user.billingCode = activeSubscription?.plan?.billingCode;
    user.previousBillingCode = getSubscriptionBillingCode(previousSubscription);
    user.paywallLocation = paywallLocation;
    user.accountCountryCode = accountCountryCode;
    user.daysSinceLastSubscription = daysSinceLastSubscription;
    user.daysSinceTrialStarted = getDaysSinceTrialStarted(activeSubscription);
  }

  user.windowWidth = window.innerWidth;

  return user;
};
